import { Navigate, useLocation } from 'react-router-dom';
import { useAuthPrincipal } from './hooks/useAuthPrincipal';

/**
 * Route wrapper that protects public-only routes (sign-in, auth-choice).
 * Mirror of RequireAuth — same trigger, opposite direction.
 *
 * Redirects to / when the auth principal is signed in.
 * Renders nothing while the principal is still resolving — we must not
 * expose interactive OAuth CTAs before auth state is known, or an
 * already-signed-in user could click and trigger a `session_exists` throw
 * before the redirect lands. Once resolved: signed-in → redirect;
 * signed-out → render the public UI.
 *
 * The exception to "signed-in bounces home": `?reason=slack_user_not_found`
 * and `?reason=not_allowed`.
 * `useAuthPrincipal()` reports `ready` as soon as Clerk resolves — it has no
 * concept of a Slack-user mapping — so a Clerk-signed-in user the backend
 * can't map still reads as `ready` here. `lib/api.ts`'s 409 handler sends
 * exactly that user to `/sign-in?reason=slack_user_not_found` on purpose;
 * bouncing them straight back to `/` would remount the authenticated shell,
 * re-fire the same 409, and land back here — an infinite loop. Letting this
 * one reason through renders SignIn's existing "contact blake.johnson"
 * copy instead. `not_allowed` is the same loop for `lib/api.ts`'s 403
 * `forbidden_domain` handler: an email Terra does not admit. Deliberately
 * NOT a 4th `useAuthPrincipal` status: this is a terminal UI case reachable via one route-level check, not a readiness
 * concept every consumer of the hook needs to know about.
 */
export default function RequireSignedOut({ children }: { children: React.ReactNode }) {
  const { status } = useAuthPrincipal();
  const location = useLocation();

  if (status === 'resolving') {
    return null;
  }

  if (status === 'ready') {
    const reason = new URLSearchParams(location.search).get('reason');
    if (reason === 'slack_user_not_found' || reason === 'not_allowed') {
      return <>{children}</>;
    }
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
